/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { MoovCore } from "../core.js";
import { encodeFormQuery, encodeSimple } from "../lib/encodings.js";
import * as M from "../lib/matchers.js";
import { compactMap } from "../lib/primitives.js";
import { safeParse } from "../lib/schemas.js";
import { RequestOptions } from "../lib/sdks.js";
import { pathToFunc } from "../lib/url.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { MoovError } from "../models/errors/mooverror.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import * as operations from "../models/operations/index.js";
import { APICall, APIPromise } from "../types/async.js";
import { Result } from "../types/fp.js";

export enum GetPublicAcceptEnum {
  imageJpeg = "image/jpeg",
  imagePng = "image/png",
  imageWebp = "image/webp",
}

/**
 * Get an image by its public ID.
 */
export function imagesGetPublic(
  client: MoovCore,
  request: operations.GetPublicImageRequest,
  options?: RequestOptions & { acceptHeaderOverride?: GetPublicAcceptEnum },
): APIPromise<
  Result<
    operations.GetPublicImageResponse | undefined,
    | MoovError
    | ResponseValidationError
    | ConnectionError
    | RequestAbortedError
    | RequestTimeoutError
    | InvalidRequestError
    | UnexpectedClientError
    | SDKValidationError
  >
> {
  return new APIPromise($do(
    client,
    request,
    options,
  ));
}

async function $do(
  client: MoovCore,
  request: operations.GetPublicImageRequest,
  options?: RequestOptions & { acceptHeaderOverride?: GetPublicAcceptEnum },
): Promise<
  [
    Result<
      operations.GetPublicImageResponse | undefined,
      | MoovError
      | ResponseValidationError
      | ConnectionError
      | RequestAbortedError
      | RequestTimeoutError
      | InvalidRequestError
      | UnexpectedClientError
      | SDKValidationError
    >,
    APICall,
  ]
> {
  const parsed = safeParse(
    request,
    (value) => operations.GetPublicImageRequest$outboundSchema.parse(value),
    "Input validation failed",
  );
  if (!parsed.ok) {
    return [parsed, { status: "invalid" }];
  }
  const payload = parsed.value;
  const body = null;

  const pathParams = {
    publicID: encodeSimple("publicID", payload.publicID, {
      explode: false,
      charEncoding: "percent",
    }),
  };

  const path = pathToFunc("/images/{publicID}")(pathParams);

  const query = encodeFormQuery({
    "size": payload.size,
  }, { explode: false });

  const headers = new Headers(compactMap({
    Accept: options?.acceptHeaderOverride
      || "image/jpeg;q=1, image/png;q=0.7, image/webp;q=0",
    "if-none-match": encodeSimple("if-none-match", payload["if-none-match"], {
      explode: false,
      charEncoding: "none",
    }),
  }));

  const context = {
    options: client._options,
    baseURL: options?.serverURL ?? client._baseURL ?? "",
    operationID: "getPublicImage",
    oAuth2Scopes: null,

    resolvedSecurity: null,

    securitySource: null,
    retryConfig: options?.retries
      || client._options.retryConfig
      || { strategy: "none" },
    retryCodes: options?.retryCodes || ["429", "500", "502", "503", "504"],
  };

  const requestRes = client._createRequest(context, {
    method: "GET",
    baseURL: options?.serverURL,
    path: path,
    headers: headers,
    query: query,
    body: body,
    userAgent: client._options.userAgent,
    timeoutMs: options?.timeoutMs || client._options.timeoutMs || -1,
  }, options);
  if (!requestRes.ok) {
    return [requestRes, { status: "invalid" }];
  }
  const req = requestRes.value;

  const doResult = await client._do(req, {
    context,
    errorCodes: ["404", "429", "4XX", "500", "502", "503", "504", "5XX"],
    retryConfig: context.retryConfig,
    retryCodes: context.retryCodes,
  });
  if (!doResult.ok) {
    return [doResult, { status: "request-error", request: req }];
  }
  const response = doResult.value;

  const responseFields = {
    HttpMeta: { Response: response, Request: req },
  };

  const [result] = await M.match<
    operations.GetPublicImageResponse | undefined,
    | MoovError
    | ResponseValidationError
    | ConnectionError
    | RequestAbortedError
    | RequestTimeoutError
    | InvalidRequestError
    | UnexpectedClientError
    | SDKValidationError
  >(
    M.stream(200, operations.GetPublicImageResponse$inboundSchema.optional(), {
      ctype: "image/webp",
      hdrs: true,
      key: "Result",
    }),
    M.stream(200, operations.GetPublicImageResponse$inboundSchema.optional(), {
      ctype: "image/png",
      hdrs: true,
      key: "Result",
    }),
    M.stream(200, operations.GetPublicImageResponse$inboundSchema.optional(), {
      ctype: "image/jpeg",
      hdrs: true,
      key: "Result",
    }),
    M.nil(304, operations.GetPublicImageResponse$inboundSchema.optional(), {
      hdrs: true,
    }),
    M.fail([404, 429]),
    M.fail([500, 502, 503, 504]),
    M.fail("4XX"),
    M.fail("5XX"),
  )(response, req, { extraFields: responseFields });
  if (!result.ok) {
    return [result, { status: "complete", request: req, response }];
  }

  return [result, { status: "complete", request: req, response }];
}
